import * as React from "react";
import { Home, ArrowLeft, RefreshCw } from "lucide-react";

const AnimatedError404 = () => {
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [mousePosition, setMousePosition] = React.useState({ x: 0, y: 0 });

  React.useEffect(() => {
    setIsLoaded(true);
  }, []);

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const x = event.clientX / window.innerWidth;
    const y = event.clientY / window.innerHeight;
    setMousePosition({ x, y });
  };

  return (
    <div
      onMouseMove={handleMouseMove}
      className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex items-center justify-center p-6 overflow-hidden relative"
    >
      {/* Animated Blobs */}
      <div
        className="absolute top-1/4 -left-12 w-64 h-64 bg-green-300/30 rounded-full blur-3xl"
        style={{
          transform: `translate(${mousePosition.x * 20}px, ${
            mousePosition.y * 20
          }px)`,
          transition: "transform 0.2s ease-out",
        }}
      />
      <div
        className="absolute bottom-1/4 -right-12 w-64 h-64 bg-emerald-300/30 rounded-full blur-3xl"
        style={{
          transform: `translate(${-mousePosition.x * 20}px, ${
            -mousePosition.y * 20
          }px)`,
          transition: "transform 0.2s ease-out",
        }}
      />

      {/* Main Content */}
      <div
        className={`relative max-w-4xl w-full text-center transition-all duration-1000 ${
          isLoaded ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"
        }`}
      >
        {/* Improved 404 Text */}
        <div className="relative mb-12">
          <h1 className="font-bold text-[100px] lg:text-[200px] leading-none bg-clip-text text-transparent bg-gradient-to-b from-green-600 to-emerald-800 select-none">
            404
          </h1>
          <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-full">
            <p className="text-3xl font-semibold text-green-950 tracking-wide mt-2">
              Page Not Found
            </p>
            <div className="w-40 h-1 bg-gradient-to-r from-green-500 to-emerald-500 mx-auto rounded-full" />
          </div>
        </div>

        {/* Message */}
        <p className="text-xl text-green-800 mb-12 max-w-lg mx-auto">
          The page you're looking for seems to have wandered off into the
          digital forest. Let's help you find your way back!
        </p>

        {/* Buttons */}
        <div className="flex flex-wrap justify-center items-center gap-6 px-4">
          <button
            onClick={() => window.history.back()}
            className="group flex items-center gap-2 px-6 py-3 bg-white/80 text-green-800 rounded-lg border border-green-100 hover:border-green-200 hover:bg-white transition-all duration-300 hover:shadow-lg hover:shadow-green-100"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            Go Back
          </button>

          <button
            onClick={() => (window.location.href = "/")}
            className="group flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:shadow-lg hover:shadow-green-200 hover:-translate-y-0.5 transition-all duration-300"
          >
            <Home className="w-5 h-5" />
            Return Home
            <div className="absolute inset-0 rounded-lg bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </button>

          <button
            onClick={() => window.location.reload()}
            className="group flex items-center gap-2 px-6 py-3 bg-white/80 text-green-800 rounded-lg border border-green-100 hover:border-green-200 hover:bg-white transition-all duration-300 hover:shadow-lg hover:shadow-green-100"
          >
            <RefreshCw className="w-5 h-5 group-hover:rotate-180 transition-transform duration-500" />
            Refresh Page
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnimatedError404;
